import type { IMetaColumn, IMetaComputed, IMetaData } from "../types/scad";
import useMetaAsync from "./useMetaAsync";

export interface MetaComposable {
  data: Ref<IMetaComputed | null>;
  rawData: Ref<IMetaData | null>;
  rawDataOverride: Ref<IMetaData | null>;
  pending: Ref<boolean>;
  refresh: (opts?: any | undefined) => Promise<any>;
  execute: (opts?: any | undefined) => Promise<any>;
  getColumn: (columnName: string) => IMetaColumn | null | undefined;
}

export default function useMeta(
  table: string,
  schema: string = "public",
  immediate: boolean = true,
): MetaComposable {
  const data = ref<IMetaComputed | null>(null);
  const rawData = ref<IMetaData | null>(null);
  const rawDataOverride = ref<IMetaData | null>(null);
  const pending = ref(immediate);

  let composable: MetaComposable | null = null;

  const init = async () => {
    composable = await useMetaAsync(table, schema, immediate);

    // Sync initial values
    data.value = composable.data.value;
    rawData.value = composable.rawData.value;
    rawDataOverride.value = composable.rawDataOverride.value;
    pending.value = composable.pending.value;

    // Set up watchers for future changes
    watch(composable.data, (v) => {
      data.value = v;
    });
    watch(composable.rawData, (v) => {
      rawData.value = v;
    });
    watch(composable.rawDataOverride, (v) => {
      rawDataOverride.value = v;
    });
    watch(composable.pending, (v) => {
      pending.value = v;
    });
  };

  const initPromise = init();

  const refresh = async (opts?: any) => {
    await initPromise;
    return composable?.refresh(opts);
  };

  const execute = async (opts?: any) => {
    await initPromise;
    return composable?.execute(opts);
  };

  const getColumn = (columnName: string): IMetaColumn | null | undefined => {
    return (
      data.value?.data.Columns.find((col) => col.ColumnNamePc === columnName) ??
      null
    );
  };

  return {
    data,
    rawData,
    rawDataOverride,
    pending,
    refresh,
    execute,
    getColumn,
  };
}
